import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { PropsWithChildren, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import {
  AnyRewardConfig,
  Variant,
} from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isTypes } from '@sorare/core/src/lib/gql';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import type { CollectionTaskFooter_taskInterface } from './__generated__/index.graphql.ts';

const Root = styled(Vertical)`
  border: 1px solid var(--c-nd-150);
  border-radius: var(--double-unit);
  padding: var(--intermediate-unit);
  background: var(--c-black);
`;
const LeftAligned = styled(Horizontal)`
  align-self: flex-end;
`;
const RightAlignedHorizontal = styled(Horizontal)`
  justify-content: space-between;

  @media ${laptopAndAbove} {
    justify-content: flex-end;
  }
`;

type Props = {
  collectionTask: CollectionTaskFooter_taskInterface;
  targetIcon?: ReactNode;
};
export const CollectionTaskFooter = ({
  collectionTask,
  targetIcon,
  children,
}: PropsWithChildren<Props>) => {
  if (
    !isTypes(collectionTask, ['CardsCountTask', 'CompleteCollectionsCountTask'])
  ) {
    return null;
  }

  const { liveScore, thresholds, rewardConfigs } = collectionTask;
  const rewardConfig = rewardConfigs.at(0);

  if (liveScore === 0) {
    return (
      <RightAlignedHorizontal>
        <Button size="compact" color="tertiary" disabled>
          <FormattedMessage defaultMessage="Not Started" />
        </Button>
        {!!rewardConfig && (
          <AnyRewardConfig
            anyRewardConfig={rewardConfig}
            variant={Variant.INLINE}
          />
        )}
      </RightAlignedHorizontal>
    );
  }

  const currentThresholdIndex = thresholds.findIndex(
    ({ current }) => !!current
  );

  return (
    <Root>
      <SBHorizontal>
        <Horizontal>
          <LabelM bold>
            <FormattedMessage {...glossary.reward} />
          </LabelM>
        </Horizontal>
        {!!rewardConfig && (
          <AnyRewardConfig
            anyRewardConfig={rewardConfig}
            variant={Variant.INLINE}
          />
        )}
      </SBHorizontal>
      <Vertical>
        {children}
        <LeftAligned>
          {targetIcon}
          <LabelS>{liveScore}</LabelS>
          <LabelS>/</LabelS>
          <LabelS>{thresholds[currentThresholdIndex]?.count}</LabelS>
        </LeftAligned>
      </Vertical>
    </Root>
  );
};

CollectionTaskFooter.fragments = {
  taskInterface: gql`
    fragment CollectionTaskFooter_taskInterface on TaskInterface {
      id
      rewardConfigs {
        id
        ...AnyRewardConfig_anyRewardConfigInterface
      }
      ... on CardsCountTask {
        id
        liveScore
        thresholds {
          count
          current
        }
      }
      ... on CompleteCollectionsCountTask {
        id
        liveScore
        thresholds {
          count
          current
        }
      }
    }
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<CollectionTaskFooter_taskInterface>,
};
